import _ from "lodash";
import { useEffect, useState } from "react";
import { Button, Dropdown, Form, FormProps, Input, Label, Message, Modal, Segment, Table } from "semantic-ui-react";
import { dialogControl } from "../controls/dialog-control";
import { useCurrentUser } from "../hooks/auth-hook";
import { ModalControlData, useModalControl } from "../hooks/commonHooks";
import { useFormConnect, useFormData } from "../hooks/formHooks";
import { restRequest } from "../http";
import { PageData } from "../models/common_model";
import { TestingDataBatchSerialData } from "../models/testing_setting_model";
import { fieldError, formatDateTime } from "../utils";
import { Page } from "./commons/Page";


export function TestingDataBatchSerialComponent(props: {}) {
    const currentUser = useCurrentUser();

    const [searchData, setSearchField] = useFormData<any>({});
    const [page, setSearchData, pageLoading] = useFormConnect<any, PageData<TestingDataBatchSerialData>>({}, 'testing_settings/batch_serials/search');

    const editModalControl = useModalControl<number>();

    const [products, setProducts] = useState<string[]>([]);
    useEffect(() => {
        restRequest<string[]>('svn_repository/products').then(setProducts)
    }, []);

    return <>
        <Segment.Group>
            <Segment attached="top">
                <Form onSubmit={e => {
                    e.preventDefault();
                    setSearchData(searchData);
                }} loading={pageLoading}>
                    <Form.Group>
                        <Form.Input label="Product:" inline size="small">
                            <Dropdown name="product" allowAdditions additionPosition="top" value={searchData.product || ''} onChange={setSearchField} options={[{key: '', text: 'All Product', value: ' '}].concat(_.uniq(products).map(p => ({ key: p, text: p, value: p })))}  
                            onAddItem={(e, d) => {
                                if (d.value && !products.find(p => p === d.value)) {
                                    setProducts(ps => [...ps, d.value as string]);
                                }
                            }} search selection fluid></Dropdown>
                        </Form.Input>
                        <div>&nbsp;&nbsp;<Button icon="search" type="submit"></Button></div>
                    </Form.Group>
                </Form>
            </Segment>
            <Segment attached="bottom">
                <Table striped>
                    <Table.Header>
                        <Table.Row>
                            <th>Product</th>
                            <th>Serial Pattern</th>
                            <th>Created By</th>
                            <th>Created At</th>
                            <th>{currentUser.permissions.testing.setting.filter.create && <Button color="blue" icon="add" content="Batch Serial Setting" size="mini" onClick={e => {
                                editModalControl.data = undefined;
                                editModalControl.setOpen(true);
                            }} />}</th>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {page?.items.map(obj => {
                            return <tr key={obj.id}>
                                <td>{obj.product}</td>
                                <td>{obj.serial_pattern}</td>
                                <td>{obj.created_by_name}</td>
                                <td>{formatDateTime(obj.created_at)}</td>
                                <td>
                                    {currentUser.permissions.testing.setting.batch_serial.update && <Button onClick={e => {
                                        editModalControl.data = obj.id;
                                        editModalControl.setOpen(true);
                                    }} color="blue" icon="edit" size="mini" />}

                                    {currentUser.permissions.testing.setting.batch_serial.delete && <Button onClick={e => {
                                        dialogControl.deletionConfirm(obj.product, agree => {
                                            if (agree) {
                                                return restRequest('testing_settings/batch_serials/' + obj.id, null, 'delete').then(() => setSearchData((searchData: any) => ({ ...searchData, refresh: (searchData.refresh || 1) + 1 })));
                                            }
                                            return Promise.resolve();
                                        })
                                    }} color="red" icon="delete" size="mini"></Button>}
                                </td>
                            </tr>
                        })}
                    </Table.Body>
                </Table>
                <Page data={page} updatePage={setSearchData}></Page>
            </Segment>
        </Segment.Group>

        <Editor modalControl={editModalControl} onFinish={result => {
            setSearchData((searchData: any) => ({ ...searchData, refresh: (searchData.refresh || 1) + 1 }));
            editModalControl.setOpen(false)
            setSearchData((ds: any) => ({...ds, refresh_number: (ds.refresh_number || 0) + 1}))
        }}></Editor>
    </>
}

function Editor(props: { allRoles?: { id: number, name: string }[], modalControl: ModalControlData<number | undefined>, onFinish: (data: TestingDataBatchSerialData) => void }) {
    const { modalControl: modelControl, onFinish } = props;
    const isCreate = !modelControl.data;
    const id = modelControl.data;

    const currentUser = useCurrentUser();
    const [data, setField, setData] = useFormData<TestingDataBatchSerialData>({filters: []} as any);
    const [invalidError, setInvalidError] = useState<any>({});
    const [loading, setLoading] = useState(false);

    const [products, setProducts] = useState<string[]>([]);
    useEffect(() => {
        restRequest<string[]>('svn_repository/products').then(setProducts)
    }, []);

    useEffect(() => {
        setData({product: ''} as any);
        setInvalidError({});
        if (id) {
            setLoading(true);
            restRequest<TestingDataBatchSerialData>('testing_settings/batch_serials/' + id).then(d => {
                setData(d);
            }).finally(() => setLoading(false))
        }
    }, [id]);

    const onSubmit = (event: React.FormEvent<HTMLFormElement>, formProps: FormProps) => {
        event.preventDefault();

        setLoading(true);

        restRequest<TestingDataBatchSerialData>(`testing_settings/batch_serials/${isCreate ? '' : id}`, data, isCreate ? 'post' : 'put').then(result => {
            onFinish(result);
            setInvalidError({});
        }, resp => {
            if (resp.status === 2) {
                setInvalidError(resp.data);
            }
        }).finally(() => setLoading(false));
    }


    return <>
        <Modal {...modelControl.props} size="fullscreen">
            <Modal.Header>{isCreate ? 'Create' : 'Update'} Batch Serial Settings </Modal.Header>
            <Modal.Content>
                <Form onSubmit={onSubmit} loading={loading}>
                    
                    <Form.Group widths="equal">
                        <Form.Input label="Product" error={fieldError('product', invalidError)}>
                            {isCreate ? <Dropdown name="product" allowAdditions additionPosition="top" value={data.product || ''} onChange={setField} options={_.uniq([...products, data.product || '']).filter(v => !!v).map(p => ({ key: p, text: p, value: p }))}  
                            onAddItem={(e, d) => {
                                if (d.value && !products.find(p => p === d.value)) {
                                    setProducts(ps => [...ps, d.value as string]);
                                }
                            }} search selection fluid></Dropdown> : <Input readonly>{data.product}</Input>}
                        </Form.Input>
                    </Form.Group>
                    <Form.Group widths="equal">
                        <Form.Field>
                            <label>Batch Serial Pattern</label>
                            <Message info size="small">A number to express the prefixed count of chars in serial, or "start_index~to_index" to express the specify chars in serial. You can combine multiple expression splited by ",". If there are multiple patterns of serials, every pattern started with "reg_pattern:" and seplaited with ";".
                            </Message>
                            <Form.Input name="serial_pattern" value={data.serial_pattern || ''} onChange={(e, d) => setField(null, { name: 'serial_pattern', value: d.value.trim() })} error={fieldError('serial_pattern', invalidError)}>
                            </Form.Input>
                        </Form.Field>
                    </Form.Group>
                    {(currentUser.permissions.testing.setting.filter.create || currentUser.permissions.testing.setting.filter.update) && <div>
                        <Button primary>Submit</Button>
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                        <Button as="a" onClick={e => props.modalControl.setOpen(false)}>Cancel</Button>
                    </div>}
                </Form>
            </Modal.Content>
        </Modal>
    </>
}